import React, { useEffect, useState, Component } from 'react';
import { DownloadIcon, CalendarIcon, MapPinIcon, UsersIcon, TargetIcon, TrendingUpIcon, ActivityIcon, MessageSquareIcon, CheckCircle2Icon, BarChart3Icon, HeartPulseIcon, GraduationCapIcon, LightbulbIcon, QuoteIcon, ArrowRightIcon, BuildingIcon, UserIcon } from 'lucide-react';
import { Logo, Button } from '../ui';
// --- Subcomponents for Sections ---
function SectionHeader({
  title,
  icon: Icon
}: {
  title: string;
  icon?: React.ElementType;
}) {
  return <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100" data-id="element-6339">
      {Icon && <div className="p-2 bg-brand-teal/10 text-brand-teal rounded-lg" data-id="element-6340">
          <Icon className="w-5 h-5" data-id="element-6341" />
        </div>}
      <h2 className="text-2xl font-bold text-brand-navy" data-id="element-6342">{title}</h2>
    </div>;
}
function MetricCard({
  value,
  label,
  highlight = false
}: {
  value: string | number;
  label: string;
  highlight?: boolean;
}) {
  return <div className={`p-5 rounded-2xl border ${highlight ? 'bg-brand-navy border-brand-navy text-white' : 'bg-white border-slate-100 shadow-sm'}`} data-id="element-6343">
      <div className={`text-3xl font-black mb-1 ${highlight ? 'text-brand-teal' : 'text-brand-navy'}`} data-id="element-6344">
        {value}
      </div>
      <div className={`text-sm font-medium ${highlight ? 'text-slate-300' : 'text-slate-500'}`} data-id="element-6345">
        {label}
      </div>
    </div>;
}
function ProgressBar({
  label,
  value,
  max = 100,
  colorClass = 'bg-brand-teal'
}: {
  label: string;
  value: number;
  max?: number;
  colorClass?: string;
}) {
  const percentage = Math.round(value / max * 100);
  return <div className="mb-4" data-id="element-6346">
      <div className="flex justify-between text-sm font-medium mb-1.5" data-id="element-6347">
        <span className="text-slate-700" data-id="element-6348">{label}</span>
        <span className="text-brand-navy font-bold" data-id="element-6349">
          {value}
          {max === 100 ? '%' : ''}
        </span>
      </div>
      <div className="w-full bg-slate-100 rounded-full h-2.5" data-id="element-6350">
        <div className={`h-2.5 rounded-full ${colorClass}`} style={{
        width: `${percentage}%`
      }} data-id="element-6351"></div>
      </div>
    </div>;
}
function BeforeAfterBar({
  label,
  before,
  after
}: {
  label: string;
  before: number;
  after: number;
}) {
  const max = 10;
  const beforePct = before / max * 100;
  const afterPct = after / max * 100;
  return <div className="mb-6" data-id="element-6352">
      <div className="flex justify-between text-sm font-medium mb-2" data-id="element-6353">
        <span className="text-slate-700" data-id="element-6354">{label}</span>
        <span className="text-brand-teal font-bold" data-id="element-6355">
          +{Math.round((after - before) * 10) / 10}
        </span>
      </div>
      <div className="relative h-8 bg-slate-50 rounded-lg border border-slate-100 overflow-hidden flex items-center px-2" data-id="element-6356">
        {/* Before Bar */}
        <div className="absolute left-0 top-0 bottom-0 bg-slate-200 border-r-2 border-white transition-all duration-1000" style={{
        width: `${beforePct}%`
      }} data-id="element-6357"></div>
        {/* After Bar (Delta) */}
        <div className="absolute top-0 bottom-0 bg-brand-teal/20 border-r-2 border-brand-teal transition-all duration-1000" style={{
        left: `${beforePct}%`,
        width: `${afterPct - beforePct}%`
      }} data-id="element-6358"></div>

        {/* Labels */}
        <div className="relative z-10 w-full flex justify-between text-xs font-mono" data-id="element-6359">
          <span className="text-slate-500 pl-1" data-id="element-6360">{before}</span>
          <span className="text-brand-navy font-bold pr-1" style={{
          marginLeft: `calc(${afterPct}% - 40px)`
        }} data-id="element-6361">
            {after}
          </span>
        </div>
      </div>
    </div>;
}
// --- Main Page Component ---
export function ReportPage() {
  const [activeSection, setActiveSection] = useState('overview');
  const navItems = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'goals',
    label: 'Attendee Goals'
  }, {
    id: 'engagement',
    label: 'Engagement'
  }, {
    id: 'learning',
    label: 'Learning Pathways'
  }, {
    id: 'wellness',
    label: 'Wellness'
  }, {
    id: 'feedback',
    label: 'Feedback'
  }, {
    id: 'commitments',
    label: 'Commitments'
  }, {
    id: 'recommendations',
    label: 'Recommendations'
  }, {
    id: 'export',
    label: 'Export Report'
  }];
  // Simple scroll spy
  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 200;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(navItems[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({
        top: el.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-800 flex flex-col" data-id="element-6362">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50" data-id="element-6363">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between" data-id="element-6364">
          <div className="flex items-center gap-4" data-id="element-6365">
            <Logo data-id="element-6366" />
            <div className="h-6 w-px bg-slate-200 hidden md:block" data-id="element-6367"></div>
            <span className="text-sm font-semibold text-slate-500 hidden md:block" data-id="element-6368">
              Client Report Portal
            </span>
          </div>
          <div className="flex items-center gap-4" data-id="element-6369">
            <Button variant="outline" size="sm" className="hidden sm:flex" data-id="element-6370">
              <DownloadIcon className="w-4 h-4 mr-2" data-id="element-6371" /> Download PDF
            </Button>
            <Button variant="primary" size="sm" data-id="element-6372">
              Share Report
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-[1400px] mx-auto w-full flex flex-col lg:flex-row items-start gap-8 p-6 lg:p-8" data-id="element-6373">
        {/* Sidebar Navigation */}
        <aside className="hidden lg:block w-64 shrink-0 sticky top-24" data-id="element-6374">
          <nav className="space-y-1" data-id="element-6375">
            {navItems.map(item => <button key={item.id} onClick={() => scrollTo(item.id)} className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeSection === item.id ? 'bg-brand-navy text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-brand-navy'}`} data-id="element-6376">
                {item.label}
              </button>)}
          </nav>

          <div className="mt-8 p-4 bg-brand-teal/10 rounded-xl border border-brand-teal/20" data-id="element-6377">
            <div className="text-xs font-bold text-brand-teal uppercase tracking-wider mb-2" data-id="element-6378">
              Report Status
            </div>
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700" data-id="element-6379">
              <CheckCircle2Icon className="w-4 h-4 text-brand-teal" data-id="element-6380" /> Finalized
            </div>
            <div className="text-xs text-slate-500 mt-1" data-id="element-6381">
              Generated May 16, 2026
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 space-y-12 pb-24" data-id="element-6382">
          {/* Header Section */}
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200" data-id="element-6383">
            <div className="inline-block px-3 py-1 bg-brand-navy/5 text-brand-navy text-xs font-bold rounded-full mb-4 uppercase tracking-wider" data-id="element-6384">
              Post-Event Report
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-brand-navy mb-8" data-id="element-6385">
              Leadership Reset Retreat
            </h1>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-6386">
              <div data-id="element-6387">
                <div className="text-xs text-slate-500 font-medium mb-1 flex items-center gap-1.5" data-id="element-6388">
                  <BuildingIcon className="w-3.5 h-3.5" data-id="element-6389" /> Client
                </div>
                <div className="font-semibold text-slate-800" data-id="element-6390">
                  Northstar Leadership Group
                </div>
              </div>
              <div data-id="element-6391">
                <div className="text-xs text-slate-500 font-medium mb-1 flex items-center gap-1.5" data-id="element-6392">
                  <MapPinIcon className="w-3.5 h-3.5" data-id="element-6393" /> Location
                </div>
                <div className="font-semibold text-slate-800" data-id="element-6394">
                  Punta Cana, Resort Property
                </div>
              </div>
              <div data-id="element-6395">
                <div className="text-xs text-slate-500 font-medium mb-1 flex items-center gap-1.5" data-id="element-6396">
                  <CalendarIcon className="w-3.5 h-3.5" data-id="element-6397" /> Dates
                </div>
                <div className="font-semibold text-slate-800" data-id="element-6398">
                  May 12–14, 2026
                </div>
              </div>
              <div data-id="element-6399">
                <div className="text-xs text-slate-500 font-medium mb-1 flex items-center gap-1.5" data-id="element-6400">
                  <UsersIcon className="w-3.5 h-3.5" data-id="element-6401" /> Attendees
                </div>
                <div className="font-semibold text-slate-800" data-id="element-6402">48</div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-slate-100" data-id="element-6403">
              <div className="text-xs text-slate-500 font-medium mb-3 flex items-center gap-1.5" data-id="element-6404">
                <TargetIcon className="w-3.5 h-3.5" data-id="element-6405" /> Primary Goals
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-6406">
                {['Team alignment', 'Wellness and energy', 'Leadership communication', 'Cross-functional connection'].map(goal => <span key={goal} className="px-3 py-1.5 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg" data-id="element-6407">
                    {goal}
                  </span>)}
              </div>
            </div>
          </div>

          {/* Overview Section */}
          <section id="overview" className="scroll-mt-24" data-id="element-6408">
            <SectionHeader title="Event outcomes at a glance" icon={BarChart3Icon} data-id="element-6409" />
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm mb-6" data-id="element-6410">
              <p className="text-slate-700 leading-relaxed" data-id="element-6411">
                The retreat generated strong engagement across attendee intake,
                introductions, learning prompts, wellness preferences, and
                post-event feedback. The largest improvements appeared in team
                connection, clarity, and cross-functional understanding.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-6412">
              <MetricCard value="48" label="Attendees" data-id="element-6413" />
              <MetricCard value="92%" label="Intake Completion" highlight data-id="element-6414" />
              <MetricCard value="84%" label="Hub Activation" data-id="element-6415" />
              <MetricCard value="78%" label="Intro Participation" data-id="element-6416" />
              <MetricCard value="64%" label="Wellness Activity" data-id="element-6417" />
              <MetricCard value="43" label="Commitments" data-id="element-6418" />
              <MetricCard value="8.7/10" label="Satisfaction" highlight data-id="element-6419" />
              <MetricCard value="91%" label="Would Recommend" data-id="element-6420" />
            </div>
          </section>

          {/* Before/After Section */}
          <section className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200" data-id="element-6421">
            <h3 className="text-xl font-bold text-brand-navy mb-6" data-id="element-6422">
              Before and after improvement
            </h3>
            <div className="max-w-3xl" data-id="element-6423">
              <BeforeAfterBar label="Connection to team" before={6.4} after={8.1} data-id="element-6424" />
              <BeforeAfterBar label="Clarity on priorities" before={6.2} after={8.4} data-id="element-6425" />
              <BeforeAfterBar label="Energy and wellness" before={5.8} after={7.6} data-id="element-6426" />
              <BeforeAfterBar label="Confidence in team communication" before={6.0} after={8.0} data-id="element-6427" />
              <BeforeAfterBar label="Understanding of other departments" before={5.9} after={8.3} data-id="element-6428" />
            </div>
          </section>

          {/* Attendee Goals Section */}
          <section id="goals" className="scroll-mt-24" data-id="element-6429">
            <SectionHeader title="What attendees wanted most" icon={TargetIcon} data-id="element-6430" />
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 max-w-3xl" data-id="element-6431">
              <ProgressBar label="Better communication" value={72} colorClass="bg-brand-navy" data-id="element-6432" />
              <ProgressBar label="Team alignment" value={68} colorClass="bg-brand-navy/80" data-id="element-6433" />
              <ProgressBar label="Wellness and reset" value={61} colorClass="bg-brand-teal" data-id="element-6434" />
              <ProgressBar label="Leadership clarity" value={54} colorClass="bg-brand-teal/80" data-id="element-6435" />
              <ProgressBar label="Cross-functional connection" value={49} colorClass="bg-brand-coral" data-id="element-6436" />
              <ProgressBar label="Recognition and morale" value={38} colorClass="bg-brand-coral/80" data-id="element-6437" />
            </div>

            <div className="bg-brand-teal/5 border border-brand-teal/20 p-5 rounded-2xl mt-6 max-w-3xl flex gap-3" data-id="element-6438">
              <LightbulbIcon className="w-5 h-5 text-brand-teal shrink-0 mt-0.5" data-id="element-6439" />
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-6440">
                <span className="font-semibold text-brand-navy" data-id="element-6441">Insight:</span>{' '}
                The top goals indicate that attendees were looking for both
                operational clarity and personal reset, not just social time.
              </p>
            </div>
          </section>

          {/* Engagement Section */}
          <section id="engagement" className="scroll-mt-24" data-id="element-6442">
            <SectionHeader title="Engagement across the retreat journey" icon={ActivityIcon} data-id="element-6443" />
            <div className="grid md:grid-cols-3 gap-4" data-id="element-6444">
              {[{
              phase: 'Before Event',
              bgClass: 'bg-brand-teal',
              textClass: 'text-brand-teal',
              metrics: [{
                val: '92%',
                label: 'completed intake'
              }, {
                val: '78%',
                label: 'viewed or posted introductions'
              }, {
                val: '71%',
                label: 'selected learning themes'
              }, {
                val: '64%',
                label: 'selected wellness preferences'
              }]
            }, {
              phase: 'During Event',
              bgClass: 'bg-brand-coral',
              textClass: 'text-brand-coral',
              metrics: [{
                val: '86%',
                label: 'viewed agenda'
              }, {
                val: '66%',
                label: 'joined group matching activity'
              }, {
                val: '71%',
                label: 'completed at least one reflection'
              }, {
                val: '58%',
                label: 'selected an optional activity'
              }]
            }, {
              phase: 'After Event',
              bgClass: 'bg-brand-navy',
              textClass: 'text-brand-navy',
              metrics: [{
                val: '89%',
                label: 'completed post-event survey'
              }, {
                val: '43',
                label: 'commitments captured'
              }, {
                val: '31',
                label: 'accountability actions assigned'
              }]
            }].map((phase, i) => <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col" data-id="element-6445">
                  <div className="flex items-center gap-2 mb-5 pb-4 border-b border-slate-100" data-id="element-6446">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${phase.bgClass}`} data-id="element-6447">
                      {i + 1}
                    </div>
                    <h4 className="font-bold text-brand-navy" data-id="element-6448">{phase.phase}</h4>
                  </div>
                  <div className="space-y-3 flex-1" data-id="element-6449">
                    {phase.metrics.map((m, j) => <div key={j} className="flex items-baseline gap-3" data-id="element-6450">
                        <div className={`text-xl font-bold w-14 shrink-0 ${phase.textClass}`} data-id="element-6451">
                          {m.val}
                        </div>
                        <div className="text-sm text-slate-600 font-medium leading-snug" data-id="element-6452">
                          {m.label}
                        </div>
                      </div>)}
                  </div>
                </div>)}
            </div>
          </section>

          {/* Learning Pathways Section */}
          <section id="learning" className="scroll-mt-24" data-id="element-6453">
            <SectionHeader title="Recommended learning pathway" icon={GraduationCapIcon} data-id="element-6454" />
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-6455">
              {[{
              title: 'Leadership Reset',
              focus: 'role clarity, ownership, personal operating system',
              comp: 82
            }, {
              title: 'Team Trust',
              focus: 'communication, psychological safety, cross-functional respect',
              comp: 76
            }, {
              title: 'Wellness and Energy',
              focus: 'recovery, habits, stress regulation, sleep',
              comp: 69
            }, {
              title: 'Communication Under Pressure',
              focus: 'difficult conversations, client service, conflict patterns',
              comp: 74
            }].map((mod, i) => <div key={i} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm" data-id="element-6456">
                  <div className="flex justify-between items-start mb-4" data-id="element-6457">
                    <h4 className="font-bold text-brand-navy text-lg" data-id="element-6458">
                      {mod.title}
                    </h4>
                    <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-1 rounded" data-id="element-6459">
                      Module {i + 1}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mb-6" data-id="element-6460">
                    <span className="font-semibold text-slate-700" data-id="element-6461">Focus:</span>{' '}
                    {mod.focus}
                  </p>
                  <ProgressBar label="Completion Rate" value={mod.comp} colorClass="bg-brand-coral" data-id="element-6462" />
                </div>)}
            </div>
          </section>

          {/* Wellness Section */}
          <section id="wellness" className="scroll-mt-24" data-id="element-6463">
            <SectionHeader title="Wellness and activity preferences" icon={HeartPulseIcon} data-id="element-6464" />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-6465">
              {[{
              val: '64%',
              label: 'chose morning movement'
            }, {
              val: '58%',
              label: 'chose beach walk'
            }, {
              val: '47%',
              label: 'chose quiet reflection'
            }, {
              val: '41%',
              label: 'chose spa/recovery'
            }, {
              val: '36%',
              label: 'preferred optional alcohol'
            }, {
              val: '29%',
              label: 'requested healthier food options'
            }].map((w, i) => <div key={i} className="bg-white p-5 rounded-xl border border-slate-200 text-center" data-id="element-6466">
                  <div className="text-2xl font-bold text-brand-teal mb-2" data-id="element-6467">
                    {w.val}
                  </div>
                  <div className="text-sm text-slate-600 font-medium leading-tight" data-id="element-6468">
                    {w.label}
                  </div>
                </div>)}
            </div>
          </section>

          {/* Top Themes & Insights */}
          <section className="scroll-mt-24" data-id="element-6469">
            <SectionHeader title="Top themes from participant reflections" icon={LightbulbIcon} data-id="element-6470" />

            <div className="flex flex-wrap gap-2 mb-8" data-id="element-6471">
              {['communication', 'trust', 'clarity', 'burnout', 'recognition', 'teamwork', 'accountability', 'client service', 'energy', 'priorities'].map(theme => <span key={theme} className="px-4 py-2 bg-brand-navy/5 text-brand-navy border border-brand-navy/10 rounded-full text-sm font-medium" data-id="element-6472">
                  {theme}
                </span>)}
            </div>

            <div className="grid sm:grid-cols-2 gap-4" data-id="element-6473">
              {['Team members want more clarity across departments.', 'Wellness and recovery are not just personal issues; they affect service quality.', 'Cross-functional introductions improved empathy between sales, operations, and delivery teams.', 'The team responded best to structured but low-pressure reflection.'].map((insight, i) => <div key={i} className="bg-brand-navy text-white p-6 rounded-2xl relative overflow-hidden" data-id="element-6474">
                  <div className="absolute top-0 right-0 p-4 opacity-10" data-id="element-6475">
                    <LightbulbIcon className="w-12 h-12" data-id="element-6476" />
                  </div>
                  <div className="text-brand-teal text-xs font-bold uppercase tracking-wider mb-2" data-id="element-6477">
                    Executive Insight {i + 1}
                  </div>
                  <p className="text-lg font-medium leading-snug relative z-10" data-id="element-6478">
                    {insight}
                  </p>
                </div>)}
            </div>
          </section>

          {/* Commitments */}
          <section id="commitments" className="scroll-mt-24" data-id="element-6479">
            <SectionHeader title="Commitments captured" icon={CheckCircle2Icon} data-id="element-6480" />
            <div className="grid md:grid-cols-3 gap-6" data-id="element-6481">
              <div className="bg-white p-6 rounded-2xl border border-slate-200" data-id="element-6482">
                <div className="flex items-center gap-2 mb-4 text-brand-navy font-bold" data-id="element-6483">
                  <UserIcon className="w-5 h-5" data-id="element-6484" /> Personal
                </div>
                <ul className="space-y-3 text-sm text-slate-600" data-id="element-6485">
                  <li className="flex items-start gap-2" data-id="element-6486">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-1.5 shrink-0" data-id="element-6487" />{' '}
                    Improve sleep routine
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6488">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-1.5 shrink-0" data-id="element-6489" />{' '}
                    Protect focused work time
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6490">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-1.5 shrink-0" data-id="element-6491" />{' '}
                    Communicate earlier when blocked
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6492">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-1.5 shrink-0" data-id="element-6493" />{' '}
                    Reduce reactive work habits
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-slate-200" data-id="element-6494">
                <div className="flex items-center gap-2 mb-4 text-brand-navy font-bold" data-id="element-6495">
                  <UsersIcon className="w-5 h-5" data-id="element-6496" /> Team
                </div>
                <ul className="space-y-3 text-sm text-slate-600" data-id="element-6497">
                  <li className="flex items-start gap-2" data-id="element-6498">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-coral mt-1.5 shrink-0" data-id="element-6499" />{' '}
                    Create clearer handoffs
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6500">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-coral mt-1.5 shrink-0" data-id="element-6501" />{' '}
                    Improve pre-event communication
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6502">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-coral mt-1.5 shrink-0" data-id="element-6503" />{' '}
                    Reduce last-minute escalation
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6504">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-coral mt-1.5 shrink-0" data-id="element-6505" />{' '}
                    Share client context earlier
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-slate-200" data-id="element-6506">
                <div className="flex items-center gap-2 mb-4 text-brand-navy font-bold" data-id="element-6507">
                  <TargetIcon className="w-5 h-5" data-id="element-6508" /> Leadership
                </div>
                <ul className="space-y-3 text-sm text-slate-600" data-id="element-6509">
                  <li className="flex items-start gap-2" data-id="element-6510">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-navy mt-1.5 shrink-0" data-id="element-6511" />{' '}
                    Clarify priorities
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6512">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-navy mt-1.5 shrink-0" data-id="element-6513" />{' '}
                    Improve recognition
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6514">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-navy mt-1.5 shrink-0" data-id="element-6515" />{' '}
                    Model healthier boundaries
                  </li>
                  <li className="flex items-start gap-2" data-id="element-6516">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-navy mt-1.5 shrink-0" data-id="element-6517" />{' '}
                    Create better feedback loops
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Feedback */}
          <section id="feedback" className="scroll-mt-24" data-id="element-6518">
            <SectionHeader title="Representative feedback" icon={MessageSquareIcon} data-id="element-6519" />
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-6520">
              {['This felt more intentional than a regular company trip.', 'I connected with people I normally only message for work.', 'The prompts made the conversations easier without making it feel forced.', 'The wellness options helped make the retreat feel more balanced.'].map((quote, i) => <div key={i} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm" data-id="element-6521">
                  <QuoteIcon className="w-8 h-8 text-slate-200 mb-3" data-id="element-6522" />
                  <p className="text-slate-700 font-medium italic" data-id="element-6523">"{quote}"</p>
                </div>)}
            </div>
          </section>

          {/* Recommendations */}
          <section id="recommendations" className="scroll-mt-24" data-id="element-6524">
            <SectionHeader title="Recommended next steps" icon={ArrowRightIcon} data-id="element-6525" />
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200" data-id="element-6526">
              <h3 className="text-lg font-bold text-brand-navy mb-6" data-id="element-6527">
                30-day follow-up plan
              </h3>
              <div className="space-y-6" data-id="element-6528">
                {[{
                w: 1,
                text: 'Share commitments and assign accountability partners.'
              }, {
                w: 2,
                text: 'Run a 30-minute team check-in on handoffs and communication.'
              }, {
                w: 3,
                text: 'Review wellness and workload patterns with team leads.'
              }, {
                w: 4,
                text: 'Measure progress against commitments and identify repeatable practices.'
              }].map(step => <div key={step.w} className="flex gap-4 items-start" data-id="element-6529">
                    <div className="bg-brand-teal/10 text-brand-teal font-bold px-3 py-1 rounded-lg shrink-0 text-sm" data-id="element-6530">
                      Week {step.w}
                    </div>
                    <div className="pt-1 text-slate-700 font-medium" data-id="element-6531">
                      {step.text}
                    </div>
                  </div>)}
              </div>
            </div>
          </section>

          {/* Executive Summary */}
          <section className="bg-slate-100 p-8 rounded-2xl border border-slate-200" data-id="element-6532">
            <h3 className="text-xl font-bold text-brand-navy mb-4" data-id="element-6533">
              Executive summary
            </h3>
            <p className="text-slate-700 leading-relaxed text-lg" data-id="element-6534">
              The retreat achieved strong engagement across intake,
              introductions, wellness preferences, learning prompts, and
              post-event survey completion. The clearest improvements were in
              team connection, clarity, and cross-functional understanding. The
              strongest next step is a 30-day accountability loop focused on
              communication, handoffs, and energy management.
            </p>
          </section>

          {/* CTA / Export Section */}
          <section id="export" className="scroll-mt-24 bg-brand-navy text-white p-10 rounded-3xl text-center relative overflow-hidden mt-16" data-id="element-6535">
            <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/20 via-transparent to-brand-coral/20" data-id="element-6536"></div>
            <div className="relative z-10" data-id="element-6537">
              <h2 className="text-3xl font-bold mb-3" data-id="element-6538">
                Want reports like this for every offsite?
              </h2>
              <p className="text-slate-300 mb-8 max-w-xl mx-auto" data-id="element-6539">
                Export, share, or turn this report into a recurring measurement
                loop for every event you run.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-6540">
                <Button variant="accent" href="/designer" data-id="element-6541">
                  Create Event Hub
                </Button>
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" data-id="element-6542">
                  Book Partner Demo
                </Button>
                <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/5" onClick={() => window.print()} data-id="element-6543">
                  <DownloadIcon className="w-4 h-4 mr-2" data-id="element-6544" /> Download PDF Report
                </Button>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>;
}